import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { TestModeProvider } from './contexts/TestModeContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { TestModeBanner } from './components/common/TestModeBanner';
import { ThemeTransitionOverlay } from './components/common/ThemeToggle';
import { Layout } from './components/layout/Layout';
import { LandingPage } from './pages/LandingPage';
import { LoginPage } from './pages/LoginPage';
import { WalletPage } from './pages/WalletPage';
import { AdminPage } from './pages/AdminPage';
import { VerificationPage } from './pages/VerificationPage';
import { TermsPage } from './pages/TermsPage';
import { PrivacyPage } from './pages/PrivacyPage';
import { OGHubPage } from './pages/OGHubPage';
import { DemoPage } from './pages/DemoPage';
import { LoadingOverlay } from './components/common/LoadingSpinner';
import { SEOHead } from './components/common/SEOHead';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false
    }
  }
});

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingOverlay message="Loading..." />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Admin Route Component
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAdmin, loading } = useAuth();

  if (loading) {
    return <LoadingOverlay message="Loading..." />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/og-hub" replace />;
  }

  return <>{children}</>;
};

// Public Route Component (redirect to wallet if logged in)
const PublicRoute: React.FC<{ children: React.ReactNode; redirectIfAuth?: boolean }> = ({
  children,
  redirectIfAuth = true
}) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingOverlay message="Loading..." />;
  }

  if (user && redirectIfAuth) {
    return <Navigate to="/og-hub" replace />;
  }

  return <>{children}</>;
};

// Scroll to top on route change
const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// App Routes
const AppRoutes: React.FC = () => {
  return (
    <>
      <ScrollToTop />
      <AnimatePresence mode="wait">
        <Routes>
        {/* Landing Page - Public (accessible even when logged in) */}
        <Route
          path="/"
          element={
            <>
              <SEOHead
                title="ParlayGround - The Digital Home for Sports Fans"
                description="Where fans sweat the moment, react together, and build their sports identity. Join the sports culture community, earn GroundTokens, and connect with fans worldwide."
                keywords="sports community, betting slips, sports fans, GroundTokens, SweatRooms, sports social network, parlay, sports betting community"
                ogType="website"
              />
              <LandingPage />
            </>
          }
        />

        {/* Demo Page - Public (for non-logged-in users to try the simulation) */}
        <Route
          path="/demo"
          element={
            <>
              <SEOHead
                title="Demo - ParlayGround"
                description="Experience ParlayGround's pick tracking and community features. Watch how a single screenshot transforms into a live, community-powered experience."
                keywords="demo, sports picks, community, ParlayGround demo"
              />
              <DemoPage />
            </>
          }
        />

        {/* Login/Signup Page */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <>
                <SEOHead
                  title="Sign In - ParlayGround"
                  description="Sign in or create your ParlayGround account. Join the sports community and start earning GroundTokens today."
                  keywords="login, sign up, create account, ParlayGround account"
                />
                <LoginPage />
              </>
            </PublicRoute>
          }
        />

        {/* Protected Routes */}
        <Route
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route
            path="/wallet"
            element={
              <>
                <SEOHead
                  title="Wallet - ParlayGround"
                  description="Manage your GroundTokens, view transaction history, and purchase GT tokens."
                  keywords="wallet, GroundTokens, GT, tokens, balance"
                />
                <WalletPage />
              </>
            }
          />
          <Route
            path="/og-hub"
            element={
              <>
                <SEOHead
                  title="OG Hub - ParlayGround"
                  description="Welcome to the OG Hub. Connect with early adopters, share ideas, and help shape the future of ParlayGround."
                  keywords="OG hub, early adopter, community, ParlayGround, sports fans"
                />
                <OGHubPage />
              </>
            }
          />
          <Route
            path="/verification"
            element={
              <AdminRoute>
                <>
                  <SEOHead
                    title="Slip Verification - ParlayGround"
                    description="Review and verify user-submitted betting slips."
                  />
                  <VerificationPage />
                </>
              </AdminRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <>
                  <SEOHead
                    title="Admin Dashboard - ParlayGround"
                    description="ParlayGround admin dashboard for managing users, transactions, and platform analytics."
                  />
                  <AdminPage />
                </>
              </AdminRoute>
            }
          />
        </Route>

        {/* Terms of Service */}
        <Route
          path="/terms"
          element={
            <>
              <SEOHead
                title="Terms of Service - ParlayGround"
                description="ParlayGround Terms of Service. Read our terms and conditions for using the platform."
              />
              <TermsPage />
            </>
          }
        />

        {/* Privacy Policy */}
        <Route
          path="/privacy"
          element={
            <>
              <SEOHead
                title="Privacy Policy - ParlayGround"
                description="ParlayGround Privacy Policy. Learn how we collect, use, and protect your data."
              />
              <PrivacyPage />
            </>
          }
        />

        {/* 404 - Redirect to landing */}
        <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AnimatePresence>
    </>
  );
};

// Main App Component
function App() {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <ThemeProvider>
            <AuthProvider>
              <TestModeProvider>
                <TestModeBanner />
                <ThemeTransitionOverlay />
                <AppRoutes />
              </TestModeProvider>
            </AuthProvider>
          </ThemeProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;
